<template>
  <div class="entity service" :class="mode">
    <h3 class="ui header" v-if="hasGeneralSettings">General settings</h3>
    <sui-segment emphasis="secondary" :color="hasGeneralErrors ? 'red' : 'green'" v-if="hasGeneralSettings">
      <sui-form-fields v-if="visibleFields.type || visibleFields.project">
        <EntityDropdown v-model="entity.type" :options="typeList" fieldName="type" ref="type" v-if="visibleFields.type" />
        <EntityDropdown v-model="entity.project" :options="projectList" fieldName="project" ref="project" v-if="visibleFields.project" />
        <sui-form-field :width="2" v-if="visibleFields.project">
          <label style="opacity: 0">.</label>
          <div class="ui tiny icon fluid button" :class="{disabled: !access.projectCreate}" data-inverted data-position="left center" data-tooltip="Create new project" @click.prevent="$modals.createProject.open({}, onCreateProject)" data-goal="SERVICE_FORM_NEW_PROJECT">
            <i class="ui large fitted icons">
              <i class="folder fitted icon" />
              <i class="inverted corner plus fitted icon" />
            </i>
          </div>
        </sui-form-field>
      </sui-form-fields>
      <sui-form-fields v-if="visibleFields.image || visibleFields.version">
        <EntityInput v-model="entity.image" :options="imageList" fieldName="image" ref="image" v-if="visibleFields.image" />
        <EntityDropdown v-model="entity.version" :options="versionList" fieldName="version" ref="version" v-if="visibleFields.version" />
      </sui-form-fields>
      <sui-message size="small" warning v-if="entity.isDb && oldEntity && entity.version !== oldEntity.version">
        <b>Warning!</b><br>
        Some databases require manual data migration to a new version. Make sure you've made a backup before changing the version.
      </sui-message>
      <EntityInput v-model="entity.name" fieldName="name" ref="name" :loading="checkingName" :externalError="nameError" v-if="visibleFields.name" />
      <EntityText v-model="entity.description" fieldName="description" ref="description" v-if="visibleFields.description" />
    </sui-segment>
    <h3 class="ui header" v-if="hasNetworkSettings">Network settings</h3>
    <sui-segment emphasis="secondary" :color="hasNetworkErrors ? 'red' : 'green'" v-if="hasNetworkSettings">
      <sui-form-fields>
        <ServiceRemoteAccess v-model="entity.remoteAccess" fieldName="remoteAccess" ref="remoteAccess" v-if="visibleFields.remoteAccess" />
        <ServicePorts v-model="entity.ports" fieldName="ports" ref="ports" v-if="visibleFields.ports" />
      </sui-form-fields>
    </sui-segment>
    <h3 class="ui header" v-if="hasDockerSettings">Docker settings</h3>
    <sui-segment emphasis="secondary" :color="hasDockerErrors ? 'red' : 'green'" v-if="hasDockerSettings">
      <sui-form-fields>
        <EntityInput v-model="entity.interface" fieldName="interface" ref="interface" v-if="visibleFields.interface" />
        <EntityButtons v-model="entity.network" fieldName="network" ref="network" v-if="visibleFields.network" />
      </sui-form-fields>
      <EntityDropdown :options="capabilitiesList" v-model="entity.capabilities" fieldName="capabilities" ref="capabilities" v-if="visibleFields.capabilities" />
      <EntityDropdown :options="fields.devices.enum" v-model="entity.devices" fieldName="devices" ref="devices" v-if="visibleFields.devices" />
      <EntityKeyValues v-model="entity.sysctls" fieldName="sysctls" ref="sysctls" v-if="visibleFields.sysctls" />
      <EntityKeyValues v-model="entity.labels" fieldName="labels" ref="labels" v-if="visibleFields.labels" />
      <EntityDropdown :options="fields.securityOpts.enum" v-model="entity.securityOpts" fieldName="securityOpts" ref="securityOpts" v-if="visibleFields.securityOpts" />

    </sui-segment>
    <h3 class="ui header" v-if="hasApplicationSettings">Application settings</h3>
    <sui-segment emphasis="secondary" :color="hasApplicationErrors ? 'red' : 'green'" v-if="hasApplicationSettings">
      <ServiceSource v-model="entity.source" fieldName="source" ref="source" />
      <br>
      <EntityDropdown :options="preinstallList" v-model="entity.preinstall" fieldName="preinstall" ref="preinstall" v-if="visibleFields.preinstall" />
      <EntityDropdown :options="extensionList" v-model="entity.extensions" fieldName="extensions" ref="extensions" v-if="visibleFields.extensions" />
      <EntityDropdown :options="peclPackageList" :allowAdditions="fields.peclPackages.allowAdditions" v-model="entity.peclPackages" fieldName="peclPackages" ref="peclPackages" v-if="visibleFields.peclPackages" />
    </sui-segment>
    <h3 class="ui header" v-if="hasDatabaseSettings">Database settings</h3>
    <sui-segment emphasis="secondary" :color="hasDatabaseErrors ? 'red' : 'green'" v-if="hasDatabaseSettings">
      <EntityButtons v-model="entity.configuration" fieldName="configuration" ref="configuration" v-if="visibleFields.configuration" />
      <sui-form-fields v-if="visibleFields.password && !(mode === 'migrating' && entity.hasPassword)">
        <sui-form-field :width="7" v-if="visibleFields.password.superuser"><label>Superuser login</label><div class="ui disabled input"><input :value="fields.password.superuser" readonly /></div></sui-form-field>
        <EntityPassword v-model="entity.password" fieldName="password" ref="password" v-if="visibleFields.password" />
      </sui-form-fields>
      <sui-form-fields v-if="visibleFields.username || fields.userPassword">
        <EntityInput v-model="entity.username" fieldName="username" ref="username" v-if="visibleFields.username" />
        <EntityPassword v-model="entity.userPassword" fieldName="userPassword" ref="userPassword" v-if="visibleFields.userPassword" />
      </sui-form-fields>
      <EntityInput v-model="entity.database" fieldName="database" ref="database" v-if="visibleFields.database" />
    </sui-segment>
    <h3 class="ui header" v-if="hasExtraSettings">Extra settings</h3>
    <sui-segment emphasis="secondary" :color="hasExtraErrors ? 'red' : 'green'" v-if="hasExtraSettings">
      <ServiceCommands v-model="entity.initialCommands" fieldName="initialCommands" ref="initialCommands" v-if="visibleFields.initialCommands" />
      <ServiceCommands v-model="entity.buildCommands" fieldName="buildCommands" ref="buildCommands" v-if="visibleFields.buildCommands" />
      <ServiceCommands v-model="entity.deployCommands" fieldName="deployCommands" ref="deployCommands" v-if="visibleFields.deployCommands" />
      <EntityInput class="monospace" v-model="entity.startCommand" fieldName="startCommand" ref="startCommand" :monospace="true" v-if="visibleFields.startCommand" />
    </sui-segment>
    <h3 class="ui header" v-if="visibleFields.env">Environment variables</h3>
    <ServiceEnv v-model="entity.env" fieldName="env" ref="env" :color="mode !== 'importing' ? (errors.env ? 'red' : 'green') : null" v-if="visibleFields.env" />
    <h3 class="ui header" v-if="visibleFields.volumes">Persistent data volumes</h3>
    <ServiceVolumes v-model="entity.volumes" fieldName="volumes" ref="volumes" :color="mode !== 'importing' ? (errors.volumes ? 'red' : 'green') : null" v-if="visibleFields.volumes" />
    <h3 class="ui header" v-if="visibleFields.crons">Cron tasks</h3>
    <ServiceTasks v-model="entity.crons" fieldName="crons" ref="crons" :color="errors.crons ? 'red' : 'green'" v-if="visibleFields.crons" />
    <h3 class="ui header" v-if="visibleFields.services">Configure services</h3>
    <ServiceServices v-model="entity.services" fieldName="services" ref="services" v-if="visibleFields.services" />
    <h3 class="ui header" v-if="visibleFields.configs">Config files</h3>
    <ServiceConfigs v-model="entity.configs" fieldName="configs" ref="configs" v-if="visibleFields.configs" />
    <h3 class="ui header" v-if="visibleFields.hosts">Hosts</h3>
    <sui-segment emphasis="secondary" :color="errors.hosts ? 'red' : 'green'" v-if="visibleFields.hosts">
      <ServiceHosts v-model="entity.hosts" fieldName="hosts" ref="hosts" />
    </sui-segment>
    <ServiceLogProvider v-model="entity.logProvider" fieldName="logProvider" ref="logProvider" v-if="visibleFields.logProvider" />
  </div>
</template>

<script>
  import debounce from 'lodash/debounce'
  import {mapGetters} from 'vuex'
  import EntityButtons from '@/components/forms/EntityButtons'
  import EntityDropdown from '@/components/forms/EntityDropdown'
  import EntityInput from '@/components/forms/EntityInput'
  import EntityKeyValues from '@/components/forms/EntityKeyValues'
  import EntityPassword from '@/components/forms/EntityPassword'
  import EntityText from '@/components/forms/EntityText'
  import ServiceCommands from '@/components/forms/ServiceCommands'
  import ServiceConfigs from '@/components/forms/ServiceConfigs'
  import ServiceHosts from '@/components/forms/ServiceHosts'
  import ServiceEnv from '@/components/forms/ServiceEnv'
  import ServiceLogProvider from '@/components/forms/ServiceLogProvider'
  import ServicePorts from '@/components/forms/ServicePorts'
  import ServiceRemoteAccess from '@/components/forms/ServiceRemoteAccess'
  import ServiceServices from '@/components/forms/ServiceServices'
  import ServiceSource from '@/components/forms/ServiceSource'
  import ServiceTasks from '@/components/forms/ServiceTasks'
  import ServiceVolumes from '@/components/forms/ServiceVolumes'
  import FormEntity from '@/mixins/FormEntity'
  import api from '@/api'
  import objects from '@/objects'

  export default {
    name: 'FormService',
    mixins: [
      FormEntity,
    ],
    components: {
      EntityButtons,
      EntityDropdown,
      EntityInput,
      EntityKeyValues,
      EntityPassword,
      EntityText,
      ServiceCommands,
      ServiceConfigs,
      ServiceHosts,
      ServiceEnv,
      ServiceLogProvider,
      ServicePorts,
      ServiceRemoteAccess,
      ServiceServices,
      ServiceSource,
      ServiceTasks,
      ServiceVolumes,
    },
    data() {
      return {
        object: 'Service',
        commonFields: ['description', 'name', 'project', 'type'],
        currentVersion: this.$attrs.value.version,
        nameError: null,
        checkingName: false,
      }
    },
    computed: {
      ...mapGetters(['access', 'actionsById', 'hostsById', 'projects']),
      hasGeneralSettings() {
        return this.visibleFields.type || this.visibleFields.project || this.visibleFields.image || this.visibleFields.version || this.visibleFields.name || this.visibleFields.description
      },
      hasNetworkSettings() {
        return this.visibleFields.remoteAccess || this.visibleFields.ports
      },
      hasDockerSettings() {
        return this.visibleFields.interface || this.visibleFields.network || this.visibleFields.capabilities || this.visibleFields.devices
      },
      hasApplicationSettings() {
        return this.visibleFields.source || this.visibleFields.preinstall || this.visibleFields.extensions || this.visibleFields.peclPackages
      },
      hasDatabaseSettings() {
        return this.visibleFields.configuration || this.visibleFields.database || this.visibleFields.password || this.visibleFields.username || this.visibleFields.userPassword
      },
      hasExtraSettings() {
        return this.visibleFields.initialCommands || this.visibleFields.buildCommands || this.visibleFields.deployCommands || this.visibleFields.startCommand
      },
      hasGeneralErrors() {
        return this.errors.type || this.errors.project || this.errors.image || this.errors.version || this.errors.name || this.errors.description
      },
      hasNetworkErrors() {
        return this.errors.remoteAccess || this.errors.ports
      },
      hasDockerErrors() {
        return this.errors.interface || this.errors.network || this.errors.capabilities || this.errors.devices
      },
      hasApplicationErrors() {
        return this.errors.source || this.errors.preinstall || this.errors.extensions || this.errors.peclPackages
      },
      hasDatabaseErrors() {
        return this.errors.configuration || this.errors.database || this.errors.password || this.errors.username || this.errors.userPassword
      },
      hasExtraErrors() {
        return this.errors.initialCommands || this.errors.buildCommands || this.errors.deployCommands || this.errors.startCommand
      },
      projectList() {
        return this.projects.map(project => {
          return {
            icon: 'grey folder',
            text: project.name,
            value: project.id,
            disabled: !this.actionsById[project.id].projectUsage,
          }
        })
      },
      typeList() {
        return objects._services.filter(service => service.active && !service.soon).map(service => {
          return {
            image: {src: service._logo},
            text: service.typeTitle,
            value: service.typeName,
          }
        })
      },
      versionList() {
        if (!this.fields.version.enum) {
          return null
        }
        const versions = this.fields.version.enum.map(version => {
          return {
            text: version,
            value: version,
          }
        })
        if (!versions.find(version => version.value === this.currentVersion)) {
          versions.push({
            text: this.currentVersion,
            value: this.currentVersion,
          })
        }
        return versions
      },
      preinstallList() {
        if (!this.fields.preinstall) {
          return []
        }
        return this.fields.preinstall.enum.map(app => {
          return {
            icon: 'download',
            text: app.split(/(\d+)/).join(' '),
            value: app,
          }
        })
      },
      extensionList() {
        if (!this.fields.extensions) {
          return []
        }
        return this.fields.extensions.enum.map(extension => {
          return {
            icon: 'archive',
            text: extension,
            value: extension,
          }
        })
      },
      peclPackageList() {
        if (!this.fields.peclPackages) {
          return []
        }
        return this.fields.peclPackages.enum.map(peclPackage => {
          return {
            icon: 'archive',
            text: peclPackage,
            value: peclPackage,
          }
        })
      },
      capabilitiesList() {
        if (!this.fields.capabilities) {
          return []
        }
        return this.fields.capabilities.enum.map(capability => {
          return {
            ...capability,
            icon: 'plus circle',
          }
        })
      },
      imageList() {
        return this.fields.image.official ? this.fields.image.official.map(image => {
          return {
            value: image,
            text: image,
            icon: 'blue docker',
          }
        }) : []
      },
    },
    watch: {
      'entity.name'(newName) {
        this.nameError = null
        if (newName && this.mode === 'creating') {
          this.checkingName = true
          this.debouncedCheckName(newName)
        }
      },
      'entity.type'(newType, oldType) {
        // if there's an old entity
        if (oldType) {
          this.createEntity()
        }
      },
      'entity.username'(newUsername, oldUsername) {
        const hasUsername = oldUsername && newUsername && this.$refs.username && this.$refs.username.changed
        const hasNoPassword = this.$refs.userPassword && !this.$refs.userPassword.changed && !this.entity.userPassword
        if (hasUsername && hasNoPassword) {
          this.$refs.userPassword.generatePassword()
        }
        if (this.$refs.database && !this.$refs.database.changed) {
          this.entity.database = this.entity.username
        }
      },
    },
    methods: {
      onCreateProject(result) {
        if (result && result.id) {
          this.entity.project = result.id
        }
      },
      debouncedCheckName: debounce(function checkName(newName) {
        api.checkServiceName(newName).catch(error => {
          this.nameError = error
        }).finally(() => {
          this.checkingName = false
        })
      }, 500, {leading: false})
    },
  }
</script>
